import React, { forwardRef, useCallback, useRef, useState } from "react";
import FeatherIcon from "feather-icons-react";
import "./ScfoButton.scss";

const VARIANT_CLASSES = {
  primary: "btn-scfo-primary",
  secondary: "btn-scfo-secondary",
  ghost: "btn-scfo-ghost",
  dark: "btn-scfo-dark",
  danger: "btn-scfo-danger",
  success: "btn-scfo-success",
};

const SIZE_CLASSES = {
  sm: "btn-scfo-sm",
  md: "",
  lg: "btn-scfo-lg",
};

/**
 * Design-system button (native button element + .btn-scfo-* tokens).
 * Prefer this over raw Semantic UI Button for new and migrated screens.
 */
const ScfoButton = forwardRef(function ScfoButton(
  {
    variant = "primary",
    size = "md",
    loading = false,
    async: asyncClick = false,
    cooldownMs = 500,
    disableOnLoading = true,
    disabled = false,
    onClick,
    type = "button",
    className = "",
    children,
    ...rest
  },
  ref
) {
  const [internalLoading, setInternalLoading] = useState(false);
  const lastClickRef = useRef(0);
  const isExecutingRef = useRef(false);

  const isLoading = Boolean(loading || internalLoading);
  const isDisabled =
    Boolean(disabled) || (disableOnLoading && isLoading);

  const handleClick = useCallback(
    async (e) => {
      if (!onClick) return;
      if (!asyncClick) {
        onClick(e);
        return;
      }
      if (isExecutingRef.current) return;
      const now = Date.now();
      if (now - lastClickRef.current < cooldownMs) return;
      if (disabled || loading) return;
      lastClickRef.current = now;
      isExecutingRef.current = true;
      setInternalLoading(true);
      try {
        await onClick(e);
      } finally {
        isExecutingRef.current = false;
        setInternalLoading(false);
      }
    },
    [onClick, asyncClick, disabled, loading, cooldownMs]
  );

  const variantClass =
    VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary;
  const sizeClass = SIZE_CLASSES[size] ?? "";
  const classes = ["btn-scfo", variantClass, sizeClass, className]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={isDisabled}
      onClick={handleClick}
      aria-busy={isLoading || undefined}
      {...rest}
    >
      {isLoading ? (
        <FeatherIcon
          icon="loader"
          size={16}
          strokeWidth={2.5}
          className="scfo-button__spinner"
          aria-hidden
        />
      ) : null}
      {children}
    </button>
  );
});

export default ScfoButton;
